/**
 * SKE-4681 — the shortening snooze.
 *
 * BROWSER STORAGE, keyed per browser, matching `ServiceStatusBanner` and
 * `SubscriptionBanner`. The ticket originally asked for a per-user snooze; that
 * was corrected, because there is no self-service preference endpoint and
 * building one to remember a dismissal is not warranted.
 *
 * The duration is NOT computed here. The server returns the snooze for the
 * current week (7 days, then 48 hours, then the session, then none at all), and
 * a second opinion in the browser is how a customer ends up dismissing a banner
 * that the server says cannot be dismissed.
 *
 * `localStorage` for the durations that outlive a tab, `sessionStorage` for the
 * session-only week 3 snooze. Both are wrapped, because storage throws in
 * private mode and with blocked cookies, and losing a dismissal is a far
 * smaller problem than throwing during a campaign.
 */

import { useCallback, useState } from "react";

export const CAMPAIGN_SNOOZE_STORAGE_KEY = "igPublishingUpdateBannerSnoozedTo";
export const CAMPAIGN_SESSION_SNOOZE_STORAGE_KEY =
  "igPublishingUpdateBannerSnoozedSession";

/** Mirrors the server's `snooze` values. */
export type CampaignSnooze = "7_days" | "48_hours" | "session" | "none";

function readSnoozedUntil(): number {
  try {
    const raw = window.localStorage.getItem(CAMPAIGN_SNOOZE_STORAGE_KEY);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function readSessionSnoozed(): boolean {
  try {
    return (
      window.sessionStorage.getItem(CAMPAIGN_SESSION_SNOOZE_STORAGE_KEY) ===
      "true"
    );
  } catch {
    return false;
  }
}

export interface CampaignSnoozeState {
  /** True while a live snooze is hiding the banner. */
  isSnoozed: boolean;
  /** No-op when the server says this register cannot be dismissed. */
  snooze: () => void;
}

export function useCampaignSnooze(
  snooze: CampaignSnooze | undefined,
  snoozeMs: number | null | undefined,
  now: () => number = Date.now
): CampaignSnoozeState {
  const [snoozedUntil, setSnoozedUntil] = useState(readSnoozedUntil);
  const [sessionSnoozed, setSessionSnoozed] = useState(readSessionSnoozed);

  const applySnooze = useCallback(() => {
    // The final 72 hours. The server said no, and the browser does not get a
    // vote: this is the one register the customer cannot put away.
    if (snooze === "none" || !snooze) return;

    if (snooze === "session") {
      try {
        window.sessionStorage.setItem(
          CAMPAIGN_SESSION_SNOOZE_STORAGE_KEY,
          "true"
        );
      } catch {
        // Storage unavailable. The banner stays; that is the safe direction.
      }
      setSessionSnoozed(true);
      return;
    }

    /*
     * A dated register with no duration would compute `now() + 0`, an
     * already-expired stamp, and the dismiss button would do nothing at all.
     * Falling back to the session is the honest failure: the customer's click
     * has an effect, and it is the shortest one.
     */
    if (!snoozeMs) {
      try {
        window.sessionStorage.setItem(
          CAMPAIGN_SESSION_SNOOZE_STORAGE_KEY,
          "true"
        );
      } catch {
        // Storage unavailable. The banner stays; that is the safe direction.
      }
      setSessionSnoozed(true);
      return;
    }

    const until = now() + snoozeMs;
    try {
      window.localStorage.setItem(CAMPAIGN_SNOOZE_STORAGE_KEY, String(until));
    } catch {
      // As above.
    }
    setSnoozedUntil(until);
  }, [snooze, snoozeMs, now]);

  /*
   * THE STORED STAMP IS CLAMPED TO THE CURRENT REGISTER'S WINDOW.
   *
   * One absolute timestamp is stored, with no week in the key, so without this
   * a dismissal keeps running at its original length after the register
   * escalates. Weeks are exactly seven days, so a week 1 dismissal on day 5
   * would suppress the banner five days into week 2, and a week 2 dismissal
   * could eat the first two days of the red week 3 register. The customer
   * would have silenced a calmer message than the one being withheld.
   *
   * Reading a stamp further out than the CURRENT register allows treats it as
   * spent. Escalation therefore shortens an existing snooze without needing a
   * second key or a migration, and the final 72 hours ignore it entirely
   * because `snooze` is "none" there.
   */
  const withinCurrentWindow =
    typeof snoozeMs === "number" ? snoozedUntil - now() <= snoozeMs : true;

  const isSnoozed =
    snooze === "none"
      ? false
      : snooze === "session"
        ? sessionSnoozed
        : // A dated register with no duration falls back to the session, so the
          // session flag has to be honoured here too or the click does nothing.
          (!snoozeMs && sessionSnoozed) ||
          (snoozedUntil > now() && withinCurrentWindow);

  return { isSnoozed, snooze: applySnooze };
}
